import React, {
	ChangeEvent, KeyboardEvent, FC, useEffect, useMemo, ClipboardEvent,
} from 'react';

import {
	AsyncCellSearch, ICellEditor,
} from '@aleph/shared-components';
import {
	CarrierOptionsQuery, RedditCarrierTargetingLabelsDocument,
} from 'wb-reddit/graphql/generated';
import { uniqBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

const CarriersCellEditor: FC<ICellEditor<CarrierOptionsQuery['options']['items']>> = (
	params,
) => {
	const initialValue = useMemo(() => params.value, []);
	const {
		data, value, setValue, editorContext, setEditorContext,
	} = params;

	const [getCarrierLabels, { loading }] = useLazyQuery(RedditCarrierTargetingLabelsDocument);

	const changeSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setEditorContext({
			...editorContext,
			search: e.target.value,
		});
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			params.stopEditing();
		}

		if (e.key === 'Escape') {
			setValue(initialValue);

			setTimeout(() => params.stopEditing());
		}
	};

	useEffect(() => {
		setEditorContext({
			...editorContext,
			adAccountId: data?.parent?.parent?.adAccount?.adAccountId,
		});
	}, [data]);

	const onPaste = async (e: ClipboardEvent<HTMLInputElement>) => {
		e.stopPropagation();
		e.preventDefault();

		const carrierIds = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		try {
			const { data: carriersData } = await getCarrierLabels({
				variables: {
					carrierIds,
				},
			});

			setValue(
				uniqBy(
					[
						...(value ?? []),
						...(carriersData?.targetingCarriers?.items ?? []),
					] as CarrierOptionsQuery['options']['items'],
					'value',
				),
			);
		} catch (err) {
			captureException(err);
		}
	};

	return (
		<AsyncCellSearch onChange={changeSearch}
			onKeyDown={onKeyDown}
			onPaste={onPaste}
			onPasteCapture={onPaste}
			value={editorContext?.search ?? ''}
			loading={loading}
			loaderText="Pasting..."
			placeholder='Search carriers...'
			onSubmit={() => params.stopEditing()}
			onCancel={() => params.api.stopEditing(true)}
		/>
	);
};

export default CarriersCellEditor;
